import { QueryResult, MutationResult } from "@/api/utils/types"
import { HasuraQueries } from "@/api/hasura/queries"
import { useQuery, useMutation } from "@/api/utils/useQuery"
import { IRegistrationCreditCheck } from "@/models/operations/registrationCreditCheck/registrationCreditCheck.types"
import {
  RegistrationCreditCheckParser,
  IRegistrationCreditCheckRaw,
} from "@/models/operations/registrationCreditCheck/registrationCreditCheck.parser"
import {
  GetRegistrationCreditChecksQueryVariables,
  Registration_Credit_Check_Order_By,
  MarkCreditCheckAsDepositPaidMutationVariables,
} from "@/api/hasura/generated/graphql"
import { CreditCheckFilters } from "@/views/operations/creditChecks/opsCreditChecksReviewConfig"
import { HasuraMutations } from "@/api/hasura/mutations"

export type RegistrationCreditChecksListResult = {
  total?: number
  items?: IRegistrationCreditCheck[]
}

export type RegistrationCreditChecksResult = {
  result: IRegistrationCreditCheck[]
}

export type RegistrationCreditCheckStatusResult = {
  status?: string
}

class RegistrationCreditChecksServiceClass {
  getRegistrationCreditChecks(
    page: number,
    pageSize: number,
    orderBy: Registration_Credit_Check_Order_By,
    search: string,
    filters: CreditCheckFilters,
  ): QueryResult<RegistrationCreditChecksListResult> {
    const queryProps: GetRegistrationCreditChecksQueryVariables = {
      search: `%${search}%`,
      deadline: {},
    }

    if (filters?.depositDeadline?.from) {
      queryProps.deadline = {
        _gte: filters.depositDeadline.from,
      }
    }

    if (filters?.status?.length) {
      queryProps.status = { _in: filters.status }
    }

    if (filters?.depositDeadline?.to && filters?.depositDeadline?.from) {
      queryProps.deadline = {
        _lte: filters.depositDeadline.to,
        _gte: filters.depositDeadline.from,
      }
    }

    return useQuery({
      ...HasuraQueries.creditChecks.getRegistrationCreditChecks(
        page,
        pageSize,
        orderBy,
        { ...queryProps },
      ),
      useRawResponse: true,
      placeholderData: { items: [], total: 0 },
      parserFn: (rawData) => {
        return {
          items: rawData.registration_credit_check.map((creditCheck) =>
            RegistrationCreditCheckParser.toDomain(
              creditCheck as IRegistrationCreditCheckRaw,
            ),
          ),
          total:
            rawData.registration_credit_check_aggregate.aggregate?.count ?? 0,
        }
      },
    })
  }

  getRegistrationCreditChecksByAccountNumber(
    account_number: string,
  ): QueryResult<RegistrationCreditChecksResult> {
    return useQuery({
      ...HasuraQueries.creditChecks.getRegistrationCreditChecksByAccountNumber(
        `%${account_number}%`,
      ),
      useRawResponse: true,
      placeholderData: { result: [] },
      parserFn: (rawData) => {
        return {
          result: rawData.registration_credit_check.map((creditCheck) =>
            RegistrationCreditCheckParser.toDomain(
              creditCheck as IRegistrationCreditCheckRaw,
            ),
          ),
        }
      },
    })
  }

  markCreditCheckAsDepositPaid(): MutationResult<MarkCreditCheckAsDepositPaidMutationVariables> {
    return useMutation({
      ...HasuraMutations.creditChecks.markCreditCheckAsDepositPaid(),
      serializerFn: (data) => data,
      invalidateCache: true,
    })
  }

  getCreditCheckStatus(
    accountNumber: string,
  ): QueryResult<RegistrationCreditCheckStatusResult> {
    return useQuery({
      ...HasuraQueries.creditChecks.getRegistrationCreditCheckStatus(
        accountNumber,
      ),
      useRawResponse: true,
      placeholderData: { status: undefined },
      parserFn: (rawData) => {
        return {
          status: rawData.registration_credit_check[0]?.status,
        }
      },
    })
  }
}

export const RegistrationCreditChecksService =
  new RegistrationCreditChecksServiceClass()
